import { useEffect, useRef, useState } from 'react';

const benefits = [
  {
    icon: 'ri-flashlight-line',
    number: '3x',
    title: 'Faster Response Times',
    description: 'AI responds instantly to every inquiry'
  },
  {
    icon: 'ri-calendar-check-line',
    number: '90%',
    title: 'More Booked Appointments',
    description: 'Convert more leads into paying customers'
  },
  {
    icon: 'ri-shield-check-line',
    number: '0',
    title: 'Missed Leads',
    description: 'Never lose another opportunity again'
  },
  {
    icon: 'ri-time-line',
    number: '24/7',
    title: 'Lead Engagement',
    description: 'Your AI works around the clock'
  },
  {
    icon: 'ri-user-smile-line',
    number: '95%',
    title: 'Customer Satisfaction',
    description: 'Professional service every time'
  },
  {
    icon: 'ri-rocket-line',
    number: '5x',
    title: 'Business Growth',
    description: 'Scale without hiring more staff'
  }
];

function AnimatedCounter({ target, suffix = '' }: { target: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const numericTarget = parseInt(target.replace(/\D/g, ''));
          if (!isNaN(numericTarget)) {
            let current = 0;
            const increment = numericTarget / 50;
            const timer = setInterval(() => {
              current += increment;
              if (current >= numericTarget) {
                setCount(numericTarget);
                clearInterval(timer);
              } else {
                setCount(Math.floor(current));
              }
            }, 30);
            return () => clearInterval(timer);
          }
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [target, hasAnimated]);

  const displayValue = target.includes('x') ? `${count}x` : 
                       target.includes('%') ? `${count}%` : 
                       target === '24/7' ? '24/7' : 
                       target === '0' ? '0' : count;

  return (
    <div ref={ref} className="text-5xl md:text-6xl font-bold text-white drop-shadow-[0_0_20px_rgba(0,212,255,0.5)]">
      {displayValue}
    </div>
  );
}

export default function Benefits() {
  return (
    <section className="relative py-32 bg-gradient-to-b from-black via-[#0a0e27] to-black overflow-hidden">
      {/* Top and Bottom Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20 scroll-reveal">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            The Bayfront Advantage
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Transform your business with measurable results that drive real growth
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group scroll-reveal bg-gradient-to-br from-blue-600/5 to-cyan-400/5 backdrop-blur-xl border border-cyan-400/20 rounded-2xl p-8 transition-all duration-500 hover:bg-gradient-to-br hover:from-blue-600/10 hover:to-cyan-400/10 hover:border-cyan-400/50 hover:-translate-y-2 hover:shadow-[0_0_60px_rgba(0,212,255,0.3)] cursor-pointer"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-6 bg-gradient-to-br from-cyan-400/20 to-blue-600/20 rounded-xl border border-cyan-400/30 group-hover:scale-110 transition-all duration-500">
                <i className={`${benefit.icon} text-2xl text-cyan-400 drop-shadow-[0_0_10px_rgba(0,212,255,0.8)]`} />
              </div>

              {/* Counter */}
              <div className="text-center mb-4">
                <AnimatedCounter target={benefit.number} />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-white mb-2 text-center group-hover:text-cyan-400 transition-colors duration-300">
                {benefit.title}
              </h3>
              <p className="text-gray-400 text-center text-sm">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}